import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import TransactionalSheet from './TransactionalSheet';
import { CheckIcon, ShoppingBagIcon } from './transactional-icons';
import { ReactComponent as CardAgentIcon } from '../../../icons/card-agent.svg';
import { ReactComponent as CardVisaIcon } from '../../../icons/card-visa.svg';
import { ReactComponent as CardAmexIcon } from '../../../icons/card-amex.svg';
import { ReactComponent as CardMastercardIcon } from '../../../icons/card-mastercard.svg';
import { ReactComponent as CardOtherIcon } from '../../../icons/card-other.svg';
import { ReactComponent as CardAddIcon } from '../../../icons/card-add.svg';
import AddPaymentCardModal, {
  AddPaymentCardResult,
} from './AddPaymentCardModal';
import api from '../../../api';
import {
  PAYMENT_PRIVATE_ONLY_MESSAGE,
  PurchaseApprovalPayload,
  PurchaseApprovalRequest,
  PurchaseInstrumentChoice,
  PurchaseInstrumentOption,
  isSecurityCodeComplete,
  purchaseTotalCaveat,
  securityCodeLength,
} from './purchaseApprovalArtifact';
import './response-artifact-modal.scss';
import './spending-approval-modal.scss';
import './purchase-approval-modal.scss';

/**
 * The one real approval in the secure-payment flow: this shop, this basket,
 * this exact total, this card. It is deliberately separate from
 * SpendingApprovalModal (a cap-style pre-approval with credit readiness and
 * top-ups) — approving here binds a server-side purchase mandate through the
 * /secure-payments routes and never PATCHes the artifact. Signed in, those
 * routes are the authenticated ones; opened from the WhatsApp approval link
 * (`shareToken`), the same routes are reached through their share-token twins
 * under /public/artifacts/:shareToken, with no login. Either way the client
 * only ever sends an opaque instrument choice — never card data — plus, for a
 * saved card whose security code is not in the vault, the code typed into the
 * box on that same card row. There is no second sheet for it: a card with the
 * code on file says so, a card without one grows the box, and Approve stays
 * off until the code is complete.
 */

interface PurchaseApprovalModalProps {
  show: boolean;
  purchase: PurchaseApprovalPayload;
  /**
   * The private thread the approval card was delivered to
   * (`artifact.parentThreadId`). The server only accepts approve/reject from
   * that thread, so it travels with every decision.
   */
  threadId: string;
  assistantName?: string;
  onApproved: () => void;
  onRejected: () => void;
  onClose: () => void;
  readOnly?: boolean;
  /** Set when rendered from the no-login `/artifact/:shareToken` page. */
  shareToken?: string;
}

interface MandateSnapshot {
  purchaseMandateId: string;
  status: string;
  approvalDeadlineAt: string;
}

type SelectedInstrument =
  | { kind: 'agent_card' }
  | { kind: 'user_card'; credentialId: string }
  | { kind: 'merchant_stored' }
  | null;

type Phase = 'loading' | 'ready' | 'submitting' | 'closed';

const GENERIC_ERROR = "Couldn't reach Dream. Please try again.";
const NO_LONGER_AVAILABLE =
  'This approval is no longer available. Check your Dream chat for the latest on this purchase.';

function closedMessageForDenial(status: number): string {
  return status === 403 ? PAYMENT_PRIVATE_ONLY_MESSAGE : NO_LONGER_AVAILABLE;
}

function networkName(network: string | null): string {
  if (!network) return 'card';
  return network.charAt(0).toUpperCase() + network.slice(1);
}

function cardIcon(network: string | null) {
  switch (network) {
    case 'visa':
      return <CardVisaIcon />;
    case 'mastercard':
      return <CardMastercardIcon />;
    case 'amex':
      return <CardAmexIcon />;
    default:
      return <CardOtherIcon />;
  }
}

const cardCheck = (on: boolean) => (
  <span
    className={`card-check ${on ? 'card-check--on' : ''}`}
    aria-hidden='true'
  >
    <CheckIcon />
  </span>
);

const formatMoney = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

const formatRemaining = (deadlineAt: string, now: number) => {
  const remaining = Math.max(0, new Date(deadlineAt).getTime() - now);
  const totalSeconds = Math.floor(remaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const closedReason = (status: string): string => {
  switch (status) {
    case 'approved':
    case 'consumed':
      return 'You already approved this purchase.';
    case 'expired':
      return 'This approval window has closed. Nothing was charged.';
    case 'rejected':
      return 'You declined this purchase.';
    case 'voided':
      return 'This purchase request was withdrawn. Nothing was charged.';
    default:
      return 'This purchase request is no longer open.';
  }
};

/** The server's short `error` reason on a rejected approve, if any. */
const errorReason = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return undefined;
  }
  const response = error.response;
  if (typeof response !== 'object' || response === null) return undefined;
  if (!('data' in response)) return undefined;
  const data = response.data;
  if (typeof data !== 'object' || data === null || !('error' in data)) {
    return undefined;
  }
  return typeof data.error === 'string' ? data.error : undefined;
};

const statusCode = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return undefined;
  }
  const response = error.response;
  if (typeof response !== 'object' || response === null) return undefined;
  if (!('status' in response) || typeof response.status !== 'number') {
    return undefined;
  }
  return response.status;
};

const PurchaseApprovalModal: React.FC<PurchaseApprovalModalProps> = ({
  show,
  purchase,
  threadId,
  assistantName = 'Dream',
  onApproved,
  onRejected,
  onClose,
  readOnly = false,
  shareToken,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [phase, setPhase] = useState<Phase>('loading');
  const [closedMessage, setClosedMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [evidenceUrl, setEvidenceUrl] = useState<string | null>(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [addedCards, setAddedCards] = useState<PurchaseInstrumentOption[]>([]);
  const [selected, setSelected] = useState<SelectedInstrument>(null);
  const [securityCode, setSecurityCode] = useState('');

  const authConfig = useCallback(async () => {
    if (shareToken) return {};
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently, shareToken]);

  const mandateEndpoint = shareToken
    ? `/public/artifacts/${encodeURIComponent(shareToken)}${purchase.approveEndpoint}`
    : purchase.approveEndpoint;
  const evidenceEndpoint = shareToken
    ? `${mandateEndpoint}/evidence`
    : `/agent-files/${purchase.evidenceArtifactId}`;

  const options = useMemo(() => {
    const all = [...purchase.instrumentOptions, ...addedCards];
    return {
      agent: all.find((o) => o.type === 'agent_card') ?? null,
      saved: all.filter((o) => o.type === 'user_card' && o.ref),
      canAdd: all.some((o) => o.type === 'new_card'),
      merchant: all.find((o) => o.type === 'merchant_stored') ?? null,
    };
  }, [addedCards, purchase.instrumentOptions]);

  // Default to the user's own saved card so a single tap can approve; the
  // company card is only ever used when the user picks it.
  useEffect(() => {
    if (selected || !show) return;
    if (options.saved[0]?.ref) {
      setSelected({ kind: 'user_card', credentialId: options.saved[0].ref });
    } else if (options.agent) setSelected({ kind: 'agent_card' });
  }, [options, selected, show]);

  // The typed code never outlives the open sheet.
  useEffect(() => {
    if (!show) setSecurityCode('');
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [show]);

  // Confirm the mandate is still open before offering Approve — the card may
  // have been sitting in the thread after it expired or was already actioned.
  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setPhase('loading');
    setError(null);
    setClosedMessage(null);
    (async () => {
      try {
        const res = await api.get<MandateSnapshot>(
          mandateEndpoint,
          await authConfig(),
        );
        if (cancelled) return;
        if (res.data.status !== 'pending') {
          setClosedMessage(closedReason(res.data.status));
          setPhase('closed');
        } else {
          setPhase('ready');
        }
      } catch (err) {
        if (cancelled) return;
        const status = statusCode(err);
        if (status === 410) {
          setClosedMessage(closedReason('expired'));
        } else if (status === 404 || status === 403) {
          setClosedMessage(closedMessageForDenial(status));
        } else {
          setClosedMessage(GENERIC_ERROR);
        }
        setPhase('closed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authConfig, mandateEndpoint, show]);

  // The executor's own redacted checkout screenshot, stored as a private file.
  useEffect(() => {
    if (!show || !purchase.evidenceArtifactId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ success: boolean; downloadUrl?: string }>(
          evidenceEndpoint,
          await authConfig(),
        );
        if (!cancelled && res.data.success && res.data.downloadUrl) {
          setEvidenceUrl(res.data.downloadUrl);
        }
      } catch {
        if (!cancelled) setEvidenceUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authConfig, evidenceEndpoint, purchase.evidenceArtifactId, show]);

  const deadlinePassed = new Date(purchase.approvalDeadlineAt).getTime() <= now;

  useEffect(() => {
    if (show && phase === 'ready' && deadlinePassed) {
      setClosedMessage(closedReason('expired'));
      setPhase('closed');
    }
  }, [deadlinePassed, phase, show]);

  const instrumentChoice = (): PurchaseInstrumentChoice | null => {
    if (!selected) return null;
    if (selected.kind === 'user_card') {
      return { type: 'user_card', ref: selected.credentialId };
    }
    return { type: selected.kind };
  };

  const selectedCard =
    selected?.kind === 'user_card'
      ? (options.saved.find((c) => c.ref === selected.credentialId) ?? null)
      : null;
  // The vault has no code for this card (or could not say): ask on the row.
  const needsSecurityCode = selectedCard !== null && !selectedCard.cvcOnFile;
  const securityCodeComplete =
    !needsSecurityCode ||
    isSecurityCodeComplete(securityCode, selectedCard?.network ?? null);

  const selectCard = (credentialId: string) => {
    if (
      selected?.kind === 'user_card' &&
      selected.credentialId === credentialId
    )
      return;
    setSecurityCode('');
    setSelected({ kind: 'user_card', credentialId });
  };
  const selectOther = (kind: 'agent_card' | 'merchant_stored') => {
    setSecurityCode('');
    setSelected({ kind });
  };

  const handleApprove = async () => {
    const choice = instrumentChoice();
    if (!choice || phase !== 'ready' || readOnly || !securityCodeComplete) {
      return;
    }
    setPhase('submitting');
    setError(null);
    const body: PurchaseApprovalRequest = { threadId, instrument: choice };
    if (needsSecurityCode) body.securityCode = securityCode;
    try {
      await api.post(`${mandateEndpoint}/approve`, body, await authConfig());
      setSecurityCode('');
      onApproved();
    } catch (err) {
      const status = statusCode(err);
      const reason = errorReason(err);
      if (status === 410) {
        setClosedMessage(closedReason('expired'));
        setPhase('closed');
      } else if (status === 409) {
        setClosedMessage(closedReason('approved'));
        setPhase('closed');
      } else if (status === 403 || status === 404) {
        setClosedMessage(closedMessageForDenial(status));
        setPhase('closed');
      } else if (
        reason === 'security_code_invalid' ||
        reason === 'security_code_required'
      ) {
        setSecurityCode('');
        setError(
          `Check the ${securityCodeLength(selectedCard?.network ?? null)}-digit security code for this card and try again.`,
        );
        setPhase('ready');
      } else if (status === 422) {
        setError(
          'That card is not available for this purchase. Pick another card.',
        );
        setPhase('ready');
      } else {
        setError(GENERIC_ERROR);
        setPhase('ready');
      }
    }
  };

  const handleReject = async () => {
    if (phase !== 'ready' || readOnly) return;
    setPhase('submitting');
    setError(null);
    try {
      await api.post(
        `${mandateEndpoint}/reject`,
        { threadId },
        await authConfig(),
      );
      onRejected();
    } catch (err) {
      const status = statusCode(err);
      if (status === 410 || status === 409) {
        setClosedMessage(closedReason(status === 410 ? 'expired' : 'approved'));
        setPhase('closed');
      } else if (status === 403 || status === 404) {
        setClosedMessage(closedMessageForDenial(status));
        setPhase('closed');
      } else {
        setError(GENERIC_ERROR);
        setPhase('ready');
      }
    }
  };

  const handleCardAdded = (card: AddPaymentCardResult) => {
    setAddedCards((prev) => [
      ...prev,
      {
        type: 'user_card',
        ref: card.credentialId,
        label: card.label,
        last4: card.cardLastFour,
        network: card.cardNetwork,
        // The add-card form requires the code and the vault keeps it.
        cvcOnFile: true,
      },
    ]);
    setSecurityCode('');
    setSelected({ kind: 'user_card', credentialId: card.credentialId });
    setShowAddCard(false);
  };

  const merchantHost = purchase.merchantOrigin.replace(/^https?:\/\//, '');
  const total = formatMoney(purchase.amount, purchase.currency);
  const totalCaveat = purchaseTotalCaveat(purchase);
  const remaining = formatRemaining(purchase.approvalDeadlineAt, now);
  const cardLabel = `${assistantName}\u2019s Card`;
  const busy = phase === 'submitting' || phase === 'loading';

  const head = (
    <div className='tx-head'>
      <h1 className='tx-title' id='purchase-sheet-title'>
        Approve this purchase
      </h1>
      {phase === 'closed' ? (
        <p className='tx-sub' role='status'>
          {closedMessage}
        </p>
      ) : (
        <p className='tx-sub'>
          I've reached the checkout. Approve to pay exactly this amount at this
          shop — I'll fill in the card myself and you'll never be asked to share
          card details in chat.
        </p>
      )}
    </div>
  );

  const body =
    phase === 'closed' ? null : (
      <div className='spend-sheet-body purchase-sheet-body'>
        <div className='spend-sheet-summary'>
          <div className='spend-sheet-field'>
            <label>Shop</label>
            <div className='spend-sheet-value purchase-merchant'>
              {merchantHost}
            </div>
          </div>

          <div className='spend-sheet-field'>
            <label>Basket</label>
            <ul className='purchase-lines'>
              {purchase.lines.map((line, i) => (
                <li key={`${line.description}-${i}`}>
                  <span className='purchase-line-desc'>
                    {line.quantity > 1 ? `${line.quantity} × ` : ''}
                    {line.description}
                  </span>
                  {typeof line.lineAmount === 'number' && (
                    <span className='purchase-line-amount'>
                      {formatMoney(line.lineAmount, purchase.currency)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className='spend-sheet-field purchase-total-field'>
            <label>Total to pay</label>
            <div className='purchase-total' data-testid='purchase-total'>
              {total}
            </div>
            {totalCaveat && (
              <p
                className='purchase-total-caveat'
                role='note'
                data-testid='purchase-total-caveat'
              >
                {totalCaveat}
              </p>
            )}
            <p className='purchase-surcharge-note'>
              {purchase.surchargeNotice}
            </p>
          </div>

          {evidenceUrl && (
            <div className='spend-sheet-field'>
              <label>What I can see</label>
              <img
                className='purchase-evidence'
                src={evidenceUrl}
                alt='Redacted screenshot of the checkout page'
              />
            </div>
          )}
        </div>

        <div className='spend-sheet-card-chooser'>
          <label className='spend-sheet-chooser-label'>Pay with</label>

          {options.saved.map((card) => {
            const isSelected =
              selected?.kind === 'user_card' &&
              selected.credentialId === card.ref;
            const askCode = isSelected && !card.cvcOnFile;
            const codeLength = securityCodeLength(card.network);
            const cardName = card.last4
              ? `card ending ${card.last4}`
              : 'this card';
            return (
              <div
                key={card.ref ?? card.label ?? ''}
                className={`card-choice-row ${
                  askCode ? 'card-choice-row--with-code' : ''
                }`}
              >
                <button
                  type='button'
                  className={`card-choice card-network-${card.network || 'other'} ${
                    isSelected ? 'card-choice--selected' : ''
                  }`}
                  onClick={() => card.ref && selectCard(card.ref)}
                  disabled={busy || readOnly}
                  aria-pressed={isSelected}
                >
                  <div className='card-left'>
                    {cardIcon(card.network)}
                    <span className='card-nickname'>
                      {card.label || `Your ${networkName(card.network)}`}
                    </span>
                  </div>
                  <div className='card-right'>
                    <span className='card-right-column'>
                      <span className='card-last-four'>
                        {card.last4 ? `ending ${card.last4}` : 'saved card'}
                      </span>
                      {card.cvcOnFile === true && (
                        <span
                          className='card-cvc-status'
                          data-testid='card-cvc-on-file'
                        >
                          CVV on file
                        </span>
                      )}
                    </span>
                    {cardCheck(isSelected)}
                  </div>
                </button>
                {askCode && (
                  <div className='card-cvc-entry'>
                    <label htmlFor={`card-cvc-${card.ref}`}>
                      Security code (CVV) for {cardName}
                    </label>
                    <input
                      id={`card-cvc-${card.ref}`}
                      className='card-cvc-input'
                      type='password'
                      inputMode='numeric'
                      autoComplete='cc-csc'
                      maxLength={codeLength}
                      placeholder={'•'.repeat(codeLength)}
                      value={securityCode}
                      onChange={(e) => {
                        setError(null);
                        setSecurityCode(
                          e.target.value
                            .replace(/\D/g, '')
                            .slice(0, codeLength),
                        );
                      }}
                      disabled={busy || readOnly}
                      aria-describedby={`card-cvc-help-${card.ref}`}
                    />
                    <p
                      id={`card-cvc-help-${card.ref}`}
                      className='card-cvc-help'
                    >
                      {codeLength} digits{' '}
                      {card.network === 'amex' ? 'on the front' : 'on the back'}{' '}
                      of the card. Used once for this payment and kept with the
                      card so you're not asked again.
                    </p>
                  </div>
                )}
              </div>
            );
          })}

          {options.agent && (
            <button
              type='button'
              className={`card-choice card-network-agent ${
                selected?.kind === 'agent_card' ? 'card-choice--selected' : ''
              }`}
              onClick={() => selectOther('agent_card')}
              disabled={busy || readOnly}
              aria-pressed={selected?.kind === 'agent_card'}
            >
              <div className='card-left'>
                <CardAgentIcon />
                <span className='card-nickname'>
                  {options.agent.label || cardLabel}
                </span>
              </div>
              <div className='card-right'>
                {cardCheck(selected?.kind === 'agent_card')}
              </div>
            </button>
          )}

          {options.canAdd && (
            <button
              type='button'
              className='card-choice card-choice--add'
              onClick={() => setShowAddCard(true)}
              disabled={busy || readOnly}
            >
              <div className='card-left'>
                <CardAddIcon />
                <span className='card-nickname'>Add a payment card</span>
              </div>
            </button>
          )}

          {options.merchant && (
            <button
              type='button'
              className={`card-choice card-network-other ${
                selected?.kind === 'merchant_stored'
                  ? 'card-choice--selected'
                  : ''
              }`}
              onClick={() => selectOther('merchant_stored')}
              disabled={busy || readOnly}
              aria-pressed={selected?.kind === 'merchant_stored'}
            >
              <div className='card-left'>
                <CardOtherIcon />
                <span className='card-nickname'>
                  {options.merchant.label ||
                    `Card already saved at ${merchantHost}`}
                </span>
              </div>
              <div className='card-right'>
                {options.merchant.last4 && (
                  <span className='card-last-four'>
                    •••• {options.merchant.last4}
                  </span>
                )}
                {cardCheck(selected?.kind === 'merchant_stored')}
              </div>
            </button>
          )}
        </div>

        <p
          className={`purchase-deadline ${
            purchase.deadlineTooShort ? 'purchase-deadline--short' : ''
          }`}
          role='timer'
        >
          {deadlinePassed
            ? 'Approval window closed'
            : `Approve within ${remaining}`}
          {purchase.deadlineSource === 'site_timer' &&
            ' — the shop is holding your basket for a limited time.'}
        </p>

        {error && <div className='spend-sheet-error'>{error}</div>}
      </div>
    );

  const footer =
    phase === 'closed' ? (
      <button
        type='button'
        className='spend-sheet-approve-btn tx-btn-primary'
        onClick={onClose}
      >
        Close
      </button>
    ) : (
      <>
        <button
          type='button'
          className='spend-sheet-approve-btn tx-btn-primary'
          onClick={handleApprove}
          disabled={
            busy ||
            readOnly ||
            !selected ||
            deadlinePassed ||
            !securityCodeComplete
          }
        >
          {phase === 'submitting'
            ? 'Sending…'
            : phase === 'loading'
              ? 'Checking…'
              : `Approve ${total}`}
        </button>
        <button
          type='button'
          className='spend-sheet-reject-btn tx-btn-text'
          onClick={handleReject}
          disabled={busy || readOnly}
        >
          Reject
        </button>
      </>
    );

  return (
    <>
      <TransactionalSheet
        show={show && !showAddCard}
        onClose={onClose}
        holdOpen={phase === 'submitting'}
        leading={<ShoppingBagIcon />}
        ariaLabelledBy='purchase-sheet-title'
        className='spending-approval-modal purchase-approval-modal'
        backdropClassName='spending-approval-backdrop'
        footer={footer}
      >
        {head}
        {body}
      </TransactionalSheet>
      <AddPaymentCardModal
        show={showAddCard}
        onBack={() => setShowAddCard(false)}
        onClose={() => {
          setShowAddCard(false);
          onClose();
        }}
        onSuccess={handleCardAdded}
        shareToken={shareToken}
      />
    </>
  );
};

export default PurchaseApprovalModal;
